/**
 * 售后二维码PRO API 服务
 */
import api, { apiRequest } from '@/utils/api'

const API_BASE = '/api/qr-codes-pro'

export const qrCodeProService = {
  // 页面 CRUD
  async getPages(params = {}) {
    try {
      const pageSize = params.page_size || 20
      const response = await apiRequest.get(`${API_BASE}/pages/`, params)
      const data = response.data
      const results = data.results || (Array.isArray(data) ? data : [])
      const count = data.count ?? results.length
      return {
        success: true,
        data,
        results,
        count,
        totalPages: Math.max(1, Math.ceil(count / pageSize)),
        page: params.page || 1,
      }
    } catch (error) {
      return { success: false, message: error.response?.data?.detail || '获取页面列表失败' }
    }
  },

  async getPage(id) {
    try {
      const response = await apiRequest.get(`${API_BASE}/pages/${id}/`)
      return { success: true, data: response.data }
    } catch (error) {
      return { success: false, message: error.response?.data?.detail || '获取页面详情失败' }
    }
  },

  async createPage(data) {
    try {
      const response = await apiRequest.post(`${API_BASE}/pages/`, data)
      return { success: true, data: response.data }
    } catch (error) {
      return { success: false, message: error.response?.data?.detail || '创建页面失败' }
    }
  },

  async updatePage(id, data) {
    try {
      const response = await apiRequest.put(`${API_BASE}/pages/${id}/`, data)
      return { success: true, data: response.data }
    } catch (error) {
      return { success: false, message: error.response?.data?.detail || '更新页面失败' }
    }
  },

  async deletePage(id) {
    try {
      await apiRequest.delete(`${API_BASE}/pages/${id}/`)
      return { success: true }
    } catch (error) {
      return { success: false, message: error.response?.data?.message || error.response?.data?.detail || '删除页面失败' }
    }
  },

  async duplicatePage(id) {
    try {
      const response = await apiRequest.post(`${API_BASE}/pages/${id}/duplicate/`)
      return { success: true, data: response.data }
    } catch (error) {
      return { success: false, message: error.response?.data?.detail || '复制页面失败' }
    }
  },

  // 批次
  async getBatches(pageId) {
    try {
      const response = await apiRequest.get(`${API_BASE}/pages/${pageId}/batches/`)
      return { success: true, data: response.data }
    } catch (error) {
      return { success: false, message: '获取批次失败' }
    }
  },

  async createBatch(pageId, data) {
    try {
      const response = await apiRequest.post(`${API_BASE}/pages/${pageId}/batches/`, data)
      return { success: true, data: response.data }
    } catch (error) {
      return { success: false, message: error.response?.data?.detail || '创建批次失败' }
    }
  },

  async updateBatch(pageId, batchId, data) {
    try {
      const response = await apiRequest.patch(`${API_BASE}/batches/${batchId}/`, data)
      return { success: true, data: response.data }
    } catch (error) {
      return { success: false, message: '更新批次失败' }
    }
  },

  async deleteBatch(pageId, batchId) {
    try {
      await apiRequest.delete(`${API_BASE}/batches/${batchId}/`)
      return { success: true }
    } catch (error) {
      return { success: false, message: error.response?.data?.message || error.response?.data?.detail || '删除批次失败' }
    }
  },

  async duplicateBatch(batchId) {
    try {
      const response = await apiRequest.post(`${API_BASE}/batches/${batchId}/duplicate/`)
      return { success: true, data: response.data }
    } catch (error) {
      return { success: false, message: error.response?.data?.detail || '复制批次失败' }
    }
  },

  // 订阅记录
  async getSubscriptions(pageId) {
    try {
      const response = await apiRequest.get(`${API_BASE}/pages/${pageId}/subscriptions/`)
      return { success: true, data: response.data }
    } catch (error) {
      return { success: false, message: '获取订阅记录失败' }
    }
  },

  // 邮件模板
  async getTemplates(params = {}) {
    try {
      const response = await apiRequest.get(`${API_BASE}/templates/`, params)
      return { success: true, data: response.data.results || response.data }
    } catch (error) {
      return { success: false, message: '获取模板失败' }
    }
  },

  async createTemplate(data) {
    try {
      const response = await apiRequest.post(`${API_BASE}/templates/`, data)
      return { success: true, data: response.data }
    } catch (error) {
      return { success: false, message: '创建模板失败' }
    }
  },

  async updateTemplate(id, data) {
    try {
      const response = await apiRequest.put(`${API_BASE}/templates/${id}/`, data)
      return { success: true, data: response.data }
    } catch (error) {
      return { success: false, message: '更新模板失败' }
    }
  },

  async deleteTemplate(id) {
    try {
      await apiRequest.delete(`${API_BASE}/templates/${id}/`)
      return { success: true }
    } catch (error) {
      return { success: false, message: '删除模板失败' }
    }
  },

  // 域名
  async checkDomain(pageId, domain) {
    try {
      const response = await apiRequest.post(`${API_BASE}/pages/${pageId}/check_domain/`, { domain })
      return response.data
    } catch (error) {
      return { success: false, available: false, message: '检查域名失败' }
    }
  },

  async verifyDomain(pageId) {
    try {
      const response = await apiRequest.post(`${API_BASE}/pages/${pageId}/verify_domain/`)
      return response.data
    } catch (error) {
      return { success: false, message: '验证域名失败' }
    }
  },

  async getDomainStatus(pageId) {
    try {
      const response = await apiRequest.get(`${API_BASE}/pages/${pageId}/domain_status/`)
      return { success: true, data: response.data }
    } catch (error) {
      return { success: false, data: {} }
    }
  },

  async applySSL(pageId) {
    try {
      const response = await apiRequest.post(`${API_BASE}/pages/${pageId}/apply_ssl/`)
      return response.data
    } catch (error) {
      return { success: false, message: 'SSL申请失败' }
    }
  },

  async uploadImage(pageId, file) {
    try {
      const formData = new FormData()
      formData.append('image', file)
      const response = await apiRequest.post(`${API_BASE}/pages/${pageId}/upload_image/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      return response.data
    } catch (error) {
      return { success: false, message: error.response?.data?.message || '图片上传失败' }
    }
  },
}

export default qrCodeProService
